import type { SajuAnalysis } from '../types/input';
import type { JohooCompat } from '../types/compatibility';
import type { Ohaeng } from '../types/elements';
import type { EarthlyBranch } from '../types/branches';

type Climate = JohooCompat['person1Climate'];

const BRANCH_CLIMATE: Record<EarthlyBranch, Climate> = {
  '子': '극한', '丑': '한', '寅': '온', '卯': '온',
  '辰': '습', '巳': '열', '午': '극열', '未': '열',
  '申': '온', '酉': '온', '戌': '조', '亥': '한',
};

/** 극단적 기후(극한/극열)에서 조후 효과가 가장 크다 */
const EXTREME_CLIMATES: Climate[] = ['극한', '극열'];

/**
 * 상대 사주 오행 분포에서 조후용신 오행이 충분한지 판정한다.
 * 15%+ 비율이면 보완으로 간주.
 */
function suppliesJohoo(
  johooYongSin: Ohaeng,
  targetDist: Record<Ohaeng, number>,
): boolean {
  const total = Object.values(targetDist).reduce((s, v) => s + v, 0);
  if (total === 0) return false;
  return ((targetDist[johooYongSin] ?? 0) / total) >= 0.15;
}

/**
 * 두 사주의 조후 호환성을 분석한다.
 *
 * - 각 사주의 월지에서 기후 판정
 * - yongSinMethods에서 조후법 용신 추출
 * - 상대 오행 분포에서 조후용신이 충분한 비율인지 확인
 * - 역조후: 한쪽이 극한인데 상대가 극열의 조후용신(火→水)을 요구하는 등
 *   서로의 조후 요구가 정반대일 때 발생
 */
export function analyzeJohooCompat(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): JohooCompat {
  const monthBranch1 = analysis1.fourPillars.month.branch;
  const monthBranch2 = analysis2.fourPillars.month.branch;
  const climate1 = BRANCH_CLIMATE[monthBranch1];
  const climate2 = BRANCH_CLIMATE[monthBranch2];

  // 조후법 용신 추출 (yongSinMethods에서 조후법 찾기)
  const johu1 = analysis1.yongSinMethods.find(m => m.method === '조후');
  const johu2 = analysis2.yongSinMethods.find(m => m.method === '조후');

  // 조후법이 없으면 해당 사주는 조후 보완 대상에서 제외 (억부용신과 이중 산정 방지)
  const johooYongSin1 = johu1?.yongSin ?? analysis1.yongSinMethods[0]?.yongSin ?? '木';
  const johooYongSin2 = johu2?.yongSin ?? analysis2.yongSinMethods[0]?.yongSin ?? '木';

  const p1SuppliesP2 = johu2 ? suppliesJohoo(johooYongSin2, analysis1.ohaengDistribution) : false;
  const p2SuppliesP1 = johu1 ? suppliesJohoo(johooYongSin1, analysis2.ohaengDistribution) : false;

  // 역조후 판정: 극한↔극열 또는 한↔열 조합에서 서로의 기신이 상대의 용신과 같을 때
  const isReverse = isReverseJohoo(climate1, climate2, johu1?.giSin, johu2?.giSin, johooYongSin1, johooYongSin2);

  let harmony: JohooCompat['harmony'];
  if (isReverse) {
    harmony = '역조후';
  } else if (p1SuppliesP2 && p2SuppliesP1) {
    harmony = '완벽보완';
  } else if (p1SuppliesP2 || p2SuppliesP1) {
    harmony = '부분보완';
  } else {
    harmony = '중립';
  }

  const harmonyLabel: Record<JohooCompat['harmony'], string> = {
    '완벽보완': '상호 조후 보완 (매우 좋음)',
    '부분보완': '부분 조후 보완 (좋음)',
    '중립': '조후 영향 없음',
    '역조후': '조후 역행 (주의)',
  };

  const description = `조후: ${climate1}(${monthBranch1}월) ↔ ${climate2}(${monthBranch2}월), ` +
    `용신 ${johooYongSin1}↔${johooYongSin2} — ${harmonyLabel[harmony]}`;

  return {
    person1Climate: climate1,
    person2Climate: climate2,
    person1JohooYongSin: johooYongSin1,
    person2JohooYongSin: johooYongSin2,
    person1SuppliesPerson2: p1SuppliesP2,
    person2SuppliesPerson1: p2SuppliesP1,
    harmony,
    description,
  };
}

/**
 * 역조후 판정: 극단 기후가 대립하면서 서로의 조후용신이 상대의 기신과 같을 때
 */
function isReverseJohoo(
  c1: Climate, c2: Climate,
  giSin1: Ohaeng | undefined, giSin2: Ohaeng | undefined,
  yongSin1: Ohaeng, yongSin2: Ohaeng,
): boolean {
  // 극한↔극열, 극한↔열, 한↔극열 조합만
  const cold: Climate[] = ['극한', '한'];
  const hot: Climate[] = ['극열', '열'];
  const isOpposite = (cold.includes(c1) && hot.includes(c2)) ||
                     (hot.includes(c1) && cold.includes(c2));
  if (!isOpposite) return false;

  // 적어도 한쪽이 극단(극한/극열)이어야 역조후 효과
  if (!EXTREME_CLIMATES.includes(c1) && !EXTREME_CLIMATES.includes(c2)) return false;

  // 서로의 용신이 상대의 기신과 같으면 역조후
  const conflict1 = giSin1 !== undefined && yongSin2 === giSin1;
  const conflict2 = giSin2 !== undefined && yongSin1 === giSin2;
  return conflict1 || conflict2;
}
